<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-form ref="form" data-cy="podMenu-tools-saveFrame-form">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('routes.pod.menuLabelsAndMetadata.exportFCPXML') }}</span>
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="scrollable mt-4 pt-4">
          <div v-if="getMlLabels.length > 0 || getCustomLabels.length > 0">
            {{ $t('routes.pod.menuExportFCPXML.userMessage') }}
          </div>

          <UtilityPodsErrors data-cy="podMenu-exportFCPXML-noLabels-message"></UtilityPodsErrors>

          <div v-for="item in runnablePods" v-bind:key="item.id"
               class="my-2 d-flex justify-space-between speech-to-text-file">
            <div class="text-truncate" data-cy="podMenu-exportFCPXML-runnablePodName">
              {{ item.pod.name }}
            </div>
            <div>{{ getCustomLabels.length }} + {{ getMlLabels.length }}
              {{ $t('routes.pod.menuExportFCPXML.labels') }}
            </div>
          </div>

          <div v-show="successMessage" class="bg-success"
               data-cy="podMenu-exportFCPXML-successMessage">{{ successMessage }}
          </div>
          <div v-show="errorMessage" class="bg-error" data-cy="podMenu-exportFCPXML-errorMessage">
            {{ errorMessage }}
          </div>

        </v-card-text>

        <v-card-actions>
          <v-spacer/>

          <v-btn
            v-if="getMlLabels.length > 0 || getCustomLabels.length > 0"
            v-bind:disabled="isRunJobsDisabled"
            v-on:click.prevent="runJobs()"
            elevation=0
            color="primary"
            variant="flat"
            data-cy="podMenu-exportFCPXML-runJobs-btn"
          >
            {{ $t('common.export') }}
          </v-btn>

          <v-btn variant="text" @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import { usePodLabelStore } from '@/stores/PodLabelStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "ExportFCPXMLDialog",
  data() {
    return {
      errorMessage: '',
      successMessage: '',
      preventDoubleClick: false,
      jobIsWorking: false,
      selectedService: 'generate-FCPXML',

    };
  },
  // mounted: function() {
  // },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.updatePodsUtility();
      } else {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      this.errorMessage = '';
      this.successMessage = '';
      this.preventDoubleclick = false;
      this.jobIsWorking = false;
      this.selectedService = 'generate-FCPXML';
    },
    updatePodsUtility() {
      this.errorMessage = '';

      if (this.suitablePods.length == 0) {
        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
        return;
      }

      if (!this.getExportFCPXMLService.alias) {
        return;
      }

      const payload = {
        serviceAlias: this.getExportFCPXMLService.alias,
        pods: this.suitablePods,
      };

      const serviceStore = useServiceStore();
      serviceStore.updatePodsUtility(payload);
    },
    runJobs: function() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.getExportFCPXMLService.alias) {
        return;
      }

      const payload = {
        serviceAlias: this.getExportFCPXMLService.alias,
        utilityPods: this.runnablePods,
      };

      let self = this;
      this.preventDoubleclick = true;

      const serviceStore = useServiceStore();
      serviceStore.runService(payload)
      .then(function() {
        if (self.utilityPodsErrors.length == 0) {
          self.successMessage = self.$t("routes.pod.menuExportFCPXML.operationQueued");
        }
      })
      .catch(function(error) {
        if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
          self.errorMessage = error.response.data.error.message;
        } else {
          self.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }

        self.preventDoubleClick = false;
      });
    }
  },
  computed: {
    getMlLabels: function() {
      const podLabelStore = usePodLabelStore();
      return podLabelStore.getMlLabels;
    },
    getCustomLabels: function() {
      const podLabelStore = usePodLabelStore();
      return podLabelStore.getLabels;
    },
    suitablePods: function() {
      let suitablePods = [];
      if (this.isVideoPod) {
        const podStore = usePodStore();
        suitablePods = podStore.getSelectedVideoPods;
      }
      if (this.isAudioPod) {
        const podStore = usePodStore();
        suitablePods = podStore.getSelectedAudioPods;
      }
      if (this.isImagePod) {
        const podStore = usePodStore();
        suitablePods = podStore.getSelectedImagePods;
      }

      return suitablePods;
    },
    getUtilityPods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPods;
    },
    getExportFCPXMLService: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getExportFCPXML;
    },
    utilityPodsErrors: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    runnablePods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getRunnablePods;
    },
    isRunJobsDisabled: function() {
      // console.log(this.selectedService, this.runnablePods.length, this.preventDoubleclick);
      if (this.runnablePods.length == 0 ||
          this.preventDoubleClick == true
      ) {
        return true;
      }

      return false;
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isImagePod: function() {
      const podStore = usePodStore();
      return podStore.isImagePod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
  }

}
</script>

<style>
</style>
